<script setup lang="ts">
import type { ShopCommodityLite } from 'ld-shop-sdk'
import { getShopCommodityByPkg, initShopSdk } from 'ld-shop-sdk'
import { getAppDetailApi } from '~/api'
import type { SupportLocale } from '~/config/i18n'
import type { ReturnPromiseType } from '~/types/base'

definePageMeta({
  // 首页特殊处理透明
  middleware: () => {
    const { setConf } = useLayout()

    setConf({
      footBar: {
        visible: true,
        fixed: true,
      },
    })
  },
})

const { locale, t } = useI18n()
const route = useRoute()

const { toAppDetail, toDeveloper } = useRouterToPage()

const pageKey = computed(() => `app-detail-${route.params.seo}`)

const { value, addToCache } = await useDataCache<ReturnPromiseType<typeof getAppDetailApi>>(unref(pageKey))
const { data, error } = await useAsyncData(unref(pageKey), async () => {
  if (value) {
    return value
  }

  const { type: category, seo } = route.params as { type: string, seo: string }

  let response: ReturnPromiseType<typeof getAppDetailApi>
  // 如果是数字的时候
  if (Number.isFinite(+seo)) {
    response = await getAppDetailApi({ category, gameId: +seo })
  }
  else {
    response = await getAppDetailApi({ category, seo })
  }
  addToCache(response)
  return response
}, {
  getCachedData: (key, nuxt): ReturnPromiseType<typeof getAppDetailApi> => nuxt.payload.data[key] || nuxt.static.data[key],
  transform: (data) => {
    // 处理右上角语言切换逻辑
    const seoKeys = Object.keys(data.otherLanguageGameSeo || {}) as SupportLocale[]
    // 转换后端返回的seo字段 变成正确的url
    seoKeys.forEach((locale) => {
      const type = data.gameDetails.category
      const seo = data.otherLanguageGameSeo![locale]!
      data.otherLanguageGameSeo![locale] = toAppDetail({ type, seo }, { hasReturnUrl: true })
    })

    const titleMap: Partial<Record<SupportLocale, string>> = {
      en: '{game} for PC',
      tw: '{game}電腦版',
      kr: '{game} PC 버전',
      vn: '{game} bản PC',
      th: '{game} เวอร์ชั่น PC',
      ru: '{game} для ПК',
      pt: '{game} para PC',
      es: '{game} para PC',
      id: '{game} untuk PC',
      jp: 'PC向け{game}',
      fr: '{game} pour PC',
      de: '{game} für PC',
      ar: '{game} للكمبيوتر',
    }
    const titleKeys = Object.keys(data.otherLanguageTitle || {}) as SupportLocale[]
    titleKeys.forEach((locale) => {
      const gameName = data.otherLanguageTitle![locale]!
      data.otherLanguageTitle![locale] = titleMap[locale]?.replace('{game}', gameName)
    })

    // 处理banner图片
    const bannerImgKeys = ['profilePictureOne', 'profilePictureTwo', 'profilePictureThree', 'profilePictureFour'] as const
    data.gameDetails.headBanner = bannerImgKeys.map(i => data.gameDetails[i]).filter(Boolean)
    return data
  },
})
whenever(error, () => {
  useRouteCache(route => route.setUncacheable())
  throw createError({ statusCode: 404, statusMessage: `${unref(pageKey)} has some error`, data: { params: route.params }, fatal: true })
}, { immediate: true })

if (!isProd()) {
  whenever(data, v => clientLog.log(unref(pageKey), v), { immediate: true })
}

whenever(() => data.value?.gameDetails.jumpSeo, () => {
  const { jumpSeo, jumpTypeSeo } = data.value!.gameDetails
  return navigateTo(toAppDetail({ type: jumpTypeSeo || 'other', seo: jumpSeo }, { hasReturnUrl: true }), { redirectCode: 301, replace: true })
}, { immediate: true })

const { setLanguage, languageLoading } = useLanguageStore()
setLanguage(data.value?.otherLanguageGameSeo, data.value?.otherLanguageTitle)
languageLoading.resolve()

const currency = useLocalStorage<string>('currency', '')
const maxDiscountCommidity = ref<ShopCommodityLite>()
async function getShopCommdity() {
  const conf = await initShopSdk({
    source: 'ldxnet',
    channel: 'ldxnet',
    shareuid: '878787',
    develop: isTest() || isDev(),
    locale: locale.value,
    currency: currency.value,
  })
  // 同步货币
  currency.value = conf.currency

  // 如果有信息去查询商品 并获取最大折扣
  if (data.value) {
    const commiditys = await getShopCommodityByPkg(data.value.gameDetails.packageName)
    const sortCommidity = Object.values(commiditys || {}).flat().filter(Boolean).sort((a, b) => a.discount - b.discount)
    maxDiscountCommidity.value = sortCommidity[0]
  }
}

const { refreshJson, getInfoByPkg } = useEasyfunGameJson()

whenever(data, () => {
  getShopCommdity()
  refreshJson()
}, { immediate: true })

const easyfunGame = computed(() => getInfoByPkg(data.value?.gameDetails.packageName))

const faqList = computed(() => {
  const game = data.value?.gameDetails.gameName
  const developer = data.value?.gameDetails.developer
  const type = data.value?.gameDetails.typeName

  const q3AnsKeyList: string[] = []
  // if (easyfunGame.value) {
  //   q3AnsKeyList.push('easyfun')
  // }
  // if (maxDiscountCommidity.value) {
  //   q3AnsKeyList.push('shop')
  // }
  const q3ansKey = q3AnsKeyList.length ? `game.game_faq_a3_${q3AnsKeyList.join('_')}` : 'game.game_faq_a3'

  return [
    { q: t('game.game_faq_q1', { game }), a: t('game.game_faq_a1', { game, developer, type }) },
    { q: t('game.game_faq_q2', { game }), a: t('game.game_faq_a2', { game, developer, type }) },
    { q: t('game.game_faq_q3', { game }), a: t(q3ansKey, { game, developer, type }) },
  ]
})

// head 里面已经有判断了
// const { autoSideBar } = useLayout()
// autoSideBar()

const { appDetailHead } = usePageHead()
appDetailHead(data)

useCacheRoute()

// 第一版不加广告
const showAd = ref(false)
</script>

<template>
  <div
    v-track="{ id: 'web-apps-detail', view: { value: { page: 'apps-type-seo', ...route.params } } }"
    class="relative grid-areas-[mid] view-content"
    lt-md="pb-80px mt-20px" md="grid pt-40px pb-160px grid-areas-[left_mid_right]"
    :class="showAd ? 'grid-cols-[minmax(160px,1fr)_minmax(auto,1020px)_minmax(300px,1fr)]' : 'grid-cols-[1fr_minmax(auto,calc(100vw-40px))_1fr] lg:grid-cols-[1fr_minmax(auto,1020px)_1fr]'"
  >
    <template v-if="data">
      <div class="grid-area-[mid] w-full justify-self-center lg:px-20px">
        <GameDetailHead :value="data.gameDetails" :commidity="maxDiscountCommidity" :easyfun="easyfunGame" />

        <LdAds v-if="showAd" mt="20px md:40px" class="h-90px w-full">
          728*90
        </LdAds>

        <GameDetailContent mt="20px md:40px" :value="data.gameDetails" />

        <GameGroupBlock v-if="data.sameTypeGames.length" mt="40px md:80px" :title="$t('game.similar_games_title', { game: data.gameDetails.gameName })">
          <div class="grid" lt-md="grid-cols-4 gap-[20px_12px] p-20px pb-12px" md="grid-cols-7 gap-[24px_40px] p-40px pb-24px">
            <template v-for="item in data.sameTypeGames" :key="item.gameId">
              <GameLiteBlock v-track="{ id: 'web-apps-detail-game', click: { value: { type: '同类型游戏', name: item.gameName, seo: item.seo, pkg: item.packageName } } }" mode="light" :name="item.gameName" :icon="item.icon" name-tag="h3" :to="toAppDetail({ seo: item.seo, type: item.category }, { hasReturnUrl: true })" />
            </template>
          </div>
        </GameGroupBlock>

        <GameGroupBlock mt="40px md:80px" :title="$t('game.how_to_play', { game: data.gameDetails.gameName })">
          <GameDetailDownloadStep :value="data.gameDetails" />
        </GameGroupBlock>

        <LdAds v-if="showAd" mt="20px md:40px" class="h-90px w-full">
          728*90
        </LdAds>

        <GameGroupBlock mt="40px md:80px" :title="$t('game.advantages_by_ldx', { game: data.gameDetails.gameName })">
          <div
            class="grid gap-40px"
            lt-md="p-20px gap-[40px_16px]"
            md="grid-cols-3 p-40px text-center"
          >
            <div v-for="i in 6" :key="i" class="flex" lt-md="gap-20px" md="items-center flex-col">
              <Icon :name="`GameAdvantage${i}`" class="flex-shrink-0 c-purple-5" text="40px md:52px" />
              <div>
                <p class="c-#161636 font-700" lt-md="text-14px mb-4px" md="text-16px my-12px">
                  {{ $t(`game.advantages_title_${i}`) }}
                </p>
                <p class="c-#161636 c-op-50" lt-md="text-12px lh-18px" md="text-14px lh-20px">
                  {{ $t(`game.advantages_desc_${i}`, { game: data.gameDetails.gameName }) }}
                </p>
              </div>
            </div>
          </div>
        </GameGroupBlock>

        <GameGroupBlock mt="40px md:80px" content-class="bg-transparent" :title="$t('game.conf_head_title', { game: data.gameDetails.gameName })">
          <GameDetailConfiguration />
        </GameGroupBlock>

        <GameGroupBlock v-if="data.sameManufacturerGames?.length" mt="40px md:80px" :title="$t('game.manufacturer_games_title', { developer: data.gameDetails.developer })" :more="toDeveloper(data.gameDetails.developer, { hasReturnUrl: true })">
          <div class="grid" lt-md="grid-cols-4 gap-[20px_12px] p-20px pb-12px" md="grid-cols-7 gap-[24px_40px] p-40px pb-24px">
            <template v-for="item in data.sameManufacturerGames" :key="item.gameId">
              <GameLiteBlock v-track="{ id: 'web-apps-detail-game', click: { value: { type: '同开发者游戏', name: item.gameName, seo: item.seo, pkg: item.packageName } } }" mode="light" :name="item.gameName" :icon="item.icon" :to="toAppDetail({ seo: item.seo, type: item.category }, { hasReturnUrl: true })" />
            </template>
          </div>
        </GameGroupBlock>

        <LdAds v-if="showAd" mt="20px md:40px" class="h-90px w-full">
          728*90
        </LdAds>

        <GameGroupBlock v-if="data.guessLikeGames?.length" mt="40px md:80px" :title="$t('game.maybe_like')">
          <div class="grid" lt-md="grid-cols-4 gap-[20px_12px] p-20px pb-12px" md="grid-cols-7 gap-[24px_40px] p-40px pb-24px">
            <template v-for="item in data.guessLikeGames" :key="item.appId">
              <GameLiteBlock v-track="{ id: 'web-apps-detail-game', click: { value: { type: '猜你喜欢', name: item.gameName, seo: item.seo, pkg: item.packageName } } }" mode="light" :name="item.gameName" :icon="item.icon" :to="toAppDetail({ seo: item.seo, type: item.category }, { hasReturnUrl: true })" />
            </template>
          </div>
        </GameGroupBlock>

        <GameGroupBlock v-if="data.hotGames?.length" mt="40px md:80px" :title="$t('game.hot_games')">
          <div class="grid" lt-md="grid-cols-4 gap-[20px_12px] p-20px pb-12px" md="grid-cols-7 gap-[24px_40px] p-40px pb-24px">
            <template v-for="item in data.hotGames" :key="item.appId">
              <GameLiteBlock v-track="{ id: 'web-apps-detail-game', click: { value: { type: '热门游戏', name: item.appName, seo: item.seo, pkg: item.packageName } } }" mode="light" :name="item.appName" :icon="item.icon" :to="toAppDetail({ seo: item.seo, type: item.category }, { hasReturnUrl: true })" />
            </template>
          </div>
        </GameGroupBlock>

        <GameGroupBlock mt="40px md:80px" content-class="bg-transparent" :title="$t('game.game_faq', { game: data.gameDetails.gameName })">
          <div class="flex flex-col gap-20px">
            <GameDetailFaqGroup v-for="(i, inx) in faqList" :key="inx" :q="i.q" :a="i.a" :init-visible="inx === 0" />
          </div>
        </GameGroupBlock>
      </div>
    </template>

    <!-- left ads -->
    <div class="grid-area-[left] h-full justify-self-end lt-md:hidden">
      <div class="h-full">
        <LdAds v-if="showAd" class="sticky top-[calc(var(--header-height)+40px)] z-10 h-600px w-160px">
          160*600
        </LdAds>
      </div>
    </div>

    <!-- right ads -->
    <div class="grid-area-[right] h-full justify-self-start lt-md:hidden">
      <div class="h-full">
        <LdAds v-if="showAd" class="sticky top-[calc(var(--header-height)+40px)] z-10 h-600px w-300px">
          300*600
        </LdAds>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
</style>
